import { useEffect, useRef, useState } from 'react';
import { profileService, npcService, memoryService } from '../services/api';
import type { NpcCard } from '../services/api';
import type { ProfileCard } from '../types/social';
import type { MemoryProposal } from '../types/memory';
import { signalRService } from '../services/signalr/SignalRService';
import { HubMethods } from '../services/signalr/types';
import { GESTURE_MAP } from '@gamedate/core-character/gestures';
import { autoGestureForMessage, gestureForMessage } from '../data/chatGestures';
import { sendGameEmote, sendConversationFocus } from '../utils/gameEmote';
import { MemoryProposalCard, type ProposalCardState } from './MemoryProposalCard';
import './PlayerProfileOverlay.css';

const NPC_ID_PREFIX = 'npc_';

// Character-guided profiling (WU-W2). Hard kill-switch for the "should I remember
// that?" consent cards — set VITE_MEMORY_PROPOSALS=false to disable without a code
// change. An old backend that never emits `memoryProposal` already renders nothing.
const MEMORY_PROPOSALS_ENABLED =
  (import.meta.env.VITE_MEMORY_PROPOSALS ?? 'true') !== 'false';

// Quick gestures on the NPC chat — they play on YOUR character (you're the
// one flirting/dancing with the NPC; the NPC reacts via chat-intent rules).
const NPC_CHAT_GESTURES = ['wave', 'laughing', 'clapping', 'dance', 'blow-a-kiss', 'cheer'];

// Terminal proposal states — a decided card no longer accepts input.
const isTerminalProposalState = (s?: ProposalCardState): boolean =>
  s === 'remembered' || s === 'declined' || s === 'private';

interface NpcChatLine {
  from: 'npc' | 'me';
  text: string;
  proposal?: MemoryProposal;
  proposalState?: ProposalCardState;
}

interface PlayerProfileOverlayProps {
  /** Unity playerId = user id. */
  userId: string;
  /** Fallback name from the tap event while the card loads. */
  displayName?: string;
  /** Whether this player is currently within earshot (vicinity). */
  isNearby: boolean;
  /** Current room — needed to relay NPC gestures room-wide (npc_emote). */
  roomId?: string;
  onClose: () => void;
}

/**
 * In-game profile card — opened by tapping a player's avatar in the 3D room.
 * Deliberately independent of the social.* feature flags so profile viewing
 * from the game works even while the connections surfaces stay dark: the
 * server still filters fields by per-field visibility + viewer relationship.
 *
 * The 📞 Call action hands off to CallPanel via a window event; when the
 * target is nearby there's no need to call — you just talk in room chat.
 */
export function PlayerProfileOverlay({ userId, displayName, isNearby, roomId, onClose }: PlayerProfileOverlayProps) {
  const isNpc = userId.startsWith(NPC_ID_PREFIX);
  const [card, setCard] = useState<ProfileCard | null>(null);
  const [npc, setNpc] = useState<NpcCard | null>(null);
  const [greetingShown, setGreetingShown] = useState(false);
  const [loading, setLoading] = useState(true);
  const [npcChat, setNpcChat] = useState<NpcChatLine[]>([]);
  const [npcDraft, setNpcDraft] = useState('');
  const [npcReplying, setNpcReplying] = useState(false);
  const npcThreadRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    npcThreadRef.current?.scrollTo({ top: npcThreadRef.current.scrollHeight, behavior: 'smooth' });
  }, [npcChat, npcReplying]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setCard(null);
    setNpc(null);
    setGreetingShown(false);
    setNpcChat([]);
    setNpcDraft('');
    setNpcReplying(false);
    if (isNpc) {
      npcService.getNpcCard(userId.slice(NPC_ID_PREFIX.length)).then((n) => {
        if (cancelled) return;
        setNpc(n);
        setLoading(false);
      });
    } else {
      profileService.getProfileCardByUserId(userId).then((c) => {
        if (cancelled) return;
        setCard(c);
        setLoading(false);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [userId, isNpc]);

  const name = card?.displayName || displayName || 'Player';

  const requestCall = () => {
    window.dispatchEvent(
      new CustomEvent('gamedate-call-request', {
        detail: { userId, displayName: name },
      })
    );
    onClose();
  };

  // Relay an NPC gesture room-wide (server-validated npc_emote SocialEvent) so
  // EVERY client animates the NPC — not just the one chatting with it.
  const broadcastNpcEmote = async (gesture: string) => {
    if (!roomId) return;
    try {
      const envelopeJson = JSON.stringify({
        type: 'npc_emote',
        to: userId,
        payloadJson: JSON.stringify({ animation: gesture }),
      });
      await signalRService.invoke(HubMethods.SocialEvent, roomId, envelopeJson);
    } catch (e) {
      console.warn('[NpcChat] npc_emote relay failed:', e);
    }
  };

  // While the NPC conversation is open, frame both characters side-on
  // (conversation camera) — released on close/unmount.
  useEffect(() => {
    if (!isNpc || !greetingShown) return;
    sendConversationFocus(userId);
    return () => {
      sendConversationFocus(null);
    };
  }, [isNpc, greetingShown, userId]);

  const sendToNpc = async () => {
    const text = npcDraft.trim();
    if (!text || npcReplying || !npc) return;
    setNpcDraft('');
    setNpcChat((prev) => [...prev, { from: 'me', text }]);
    setNpcReplying(true);
    // Chat-intent: my own message can act out on MY character…
    const myGesture = autoGestureForMessage(text, 'self');
    if (myGesture) sendGameEmote(myGesture);
    // Full response surfaces the optional memoryProposal (character-guided profiling).
    // `reply` is always present (L-REPLY); `memoryProposal` is omitted when no probe fired.
    const res = await npcService.talkToNpcFull(npc.id, text);
    const reply = res.reply;
    const proposal = MEMORY_PROPOSALS_ENABLED ? res.memoryProposal : undefined;
    setNpcChat((prev) => [
      ...prev,
      {
        from: 'npc',
        text: reply || `${npc.name} is lost in thought…`,
        proposal,
        proposalState: proposal ? 'idle' : undefined,
      },
    ]);
    setNpcReplying(false);
    // …and the NPC's reply drives the NPC's body, visible to the whole room.
    const npcGesture = autoGestureForMessage(reply || '', `npc:${userId}`);
    if (npcGesture) void broadcastNpcEmote(npcGesture);
  };

  // Consent-first write (L-CONSENT): a UserMemory is created ONLY when the user taps
  // a button on the proposal card. Optimistic — jump straight to the terminal chip
  // (so the petal shows instantly), reusing the server-minted clientRef as the
  // idempotency key; revert to `error` if createMemory returns null (network/401/400).
  const handleProposalDecision = async (
    index: number,
    status: 'confirmed' | 'rejected',
    suppressTopicFamily: boolean
  ) => {
    const line = npcChat[index];
    if (!line?.proposal || line.proposalState === 'saving' || isTerminalProposalState(line.proposalState)) {
      return; // double-tap / already-decided guard
    }
    const proposal = line.proposal;
    const optimistic: ProposalCardState =
      status === 'confirmed' ? 'remembered' : suppressTopicFamily ? 'private' : 'declined';
    setNpcChat((prev) => prev.map((l, i) => (i === index ? { ...l, proposalState: optimistic } : l)));
    const saved = await memoryService.createMemory({
      clientRef: proposal.clientRef,
      npcId: proposal.npcId,
      kind: proposal.kind,
      text: proposal.text,
      tags: proposal.tags,
      polarity: proposal.polarity,
      probeId: proposal.probeId,
      source: 'conversation',
      status,
      suppressTopicFamily,
    });
    if (saved === null) {
      setNpcChat((prev) => prev.map((l, i) => (i === index ? { ...l, proposalState: 'error' } : l)));
    }
  };

  const startNpcChat = () => {
    if (!npc) return;
    setGreetingShown(true);
    setNpcChat([{ from: 'npc', text: npc.greeting }]);
    // Greeting gesture: whatever the greeting text implies, defaulting to a wave.
    void broadcastNpcEmote(gestureForMessage(npc.greeting) ?? 'wave');
  };

  if (isNpc) {
    const npcName = npc?.name || displayName || 'Mysterious stranger';
    // Once the chat starts, the card DOCKS to the side so the conversation
    // camera's side-on framing of you + the NPC stays unobstructed.
    return (
      <div className={`player-profile-overlay ${greetingShown ? 'docked' : ''}`} onClick={onClose}>
        <div className={`player-profile-card ${greetingShown ? 'docked' : ''}`} onClick={(e) => e.stopPropagation()}>
          <button className="player-profile-close" onClick={onClose} aria-label="Close">×</button>

          <div className="player-profile-head">
            <div className="player-profile-avatar placeholder npc">{npcName.charAt(0).toUpperCase()}</div>
            <div>
              <h3>{npcName}</h3>
              {npc?.role && <span className="player-profile-npc-role">{npc.role}</span>}
            </div>
          </div>

          {loading && <div className="player-profile-loading">Loading…</div>}

          {!loading && !npc && (
            <div className="player-profile-empty">
              This character keeps to themselves.
            </div>
          )}

          {npc?.backstory && !greetingShown && (
            <p className="player-profile-npc-backstory">{npc.backstory}</p>
          )}

          {npc && !greetingShown && (
            <div className="player-profile-actions">
              <button className="player-profile-sayhi-btn" onClick={startNpcChat}>
                👋 Say hi
              </button>
            </div>
          )}

          {npc && greetingShown && (
            <div className="player-profile-npc-chat">
              <div className="player-profile-npc-thread" ref={npcThreadRef}>
                {npcChat.map((line, i) => (
                  <div
                    key={i}
                    className={`player-profile-npc-bubble ${line.from === 'me' ? 'mine' : 'theirs'}`}
                  >
                    {line.from === 'npc' && (
                      <span className="player-profile-npc-greeting-name">{npcName}</span>
                    )}
                    <p>{line.text}</p>
                    {MEMORY_PROPOSALS_ENABLED && line.from === 'npc' && line.proposal && (
                      <MemoryProposalCard
                        proposal={line.proposal}
                        state={line.proposalState ?? 'idle'}
                        onDecide={(status, suppress) => handleProposalDecision(i, status, suppress)}
                      />
                    )}
                  </div>
                ))}
                {npcReplying && (
                  <div className="player-profile-npc-bubble theirs typing">
                    <span className="player-profile-npc-greeting-name">{npcName}</span>
                    <p>…</p>
                  </div>
                )}
              </div>
              <div className="player-profile-npc-gestures" role="toolbar" aria-label="Quick gestures">
                {NPC_CHAT_GESTURES.map((id) => {
                  const g = GESTURE_MAP.get(id);
                  if (!g) return null;
                  return (
                    <button
                      key={id}
                      type="button"
                      className="player-profile-npc-gesture-btn"
                      title={g.label}
                      aria-label={g.label}
                      onClick={() => sendGameEmote(id)}
                    >
                      {g.emoji}
                    </button>
                  );
                })}
              </div>
              <div className="player-profile-npc-input-row">
                <input
                  type="text"
                  value={npcDraft}
                  maxLength={500}
                  placeholder={`Answer ${npcName}…`}
                  onChange={(e) => setNpcDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') sendToNpc();
                  }}
                />
                <button
                  className="player-profile-npc-send"
                  onClick={sendToNpc}
                  disabled={!npcDraft.trim() || npcReplying}
                >
                  Send
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="player-profile-overlay" onClick={onClose}>
      <div className="player-profile-card" onClick={(e) => e.stopPropagation()}>
        <button className="player-profile-close" onClick={onClose} aria-label="Close">×</button>

        <div className="player-profile-head">
          {card?.avatarUrl ? (
            <img className="player-profile-avatar" src={card.avatarUrl} alt="" />
          ) : (
            <div className="player-profile-avatar placeholder">{name.charAt(0).toUpperCase()}</div>
          )}
          <div>
            <h3>{name}</h3>
            {card?.headline && <p className="player-profile-headline">{card.headline}</p>}
            <span className={`player-profile-presence ${isNearby ? 'near' : 'far'}`}>
              {isNearby ? '● Nearby — they can hear you in chat' : '○ Out of earshot'}
            </span>
          </div>
        </div>

        {loading && <div className="player-profile-loading">Loading profile…</div>}

        {!loading && !card && (
          <div className="player-profile-empty">
            This player hasn't set up a profile yet.
          </div>
        )}

        {card?.bio && <p className="player-profile-bio">{card.bio}</p>}

        {card && card.fields.length > 0 && (
          <div className="player-profile-fields">
            {card.fields
              .filter((f) => f.value)
              .map((f) => (
                <div key={f.key} className="player-profile-field">
                  <span className="field-label">{f.label}</span>
                  <span className="field-value">{f.value}</span>
                </div>
              ))}
          </div>
        )}

        <div className="player-profile-actions">
          {!isNearby && (
            <button className="player-profile-call-btn" onClick={requestCall}>
              📞 Call {name}
            </button>
          )}
          {isNearby && (
            <span className="player-profile-hint">Walk up and say hi in the room chat 👋</span>
          )}
        </div>
      </div>
    </div>
  );
}
